import React, { useState, useEffect } from 'react';
import { MultiplayerState, RoomListEntry } from '../types';
import { Language, t } from '../data/translations';
import RoomList from './RoomList';
import tavernBg from '../assets/ui/fantasy/tavern_bg.png';
import woodPanel from '../assets/ui/fantasy/wood_panel.png';
import parchmentTexture from '../assets/ui/fantasy/parchment_texture.png';
import btnWood from '../assets/ui/fantasy/btn_wood.png';

interface JoinRoomScreenProps {
  onJoinRoom: (roomCode: string, playerName: string, password?: string) => void;
  onBack: () => void;
  defaultPlayerName: string;
  multiplayerState: MultiplayerState;
  wsError: string | null;
  clearWsError: () => void;
  availableRooms: RoomListEntry[];
  onRefreshRooms: () => void;
  language: Language;
}

const JoinRoomScreen: React.FC<JoinRoomScreenProps> = ({
  onJoinRoom,
  onBack,
  defaultPlayerName,
  multiplayerState,
  wsError,
  clearWsError,
  availableRooms,
  onRefreshRooms,
  language
}) => {
  const [selectedRoom, setSelectedRoom] = useState<RoomListEntry | null>(null);
  const [password, setPassword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { connectionStatus } = multiplayerState;

  // Filter rooms by search query
  const filteredRooms = (availableRooms || []).filter(room =>
    room.roomName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auto-refresh room list on mount and when connected
  useEffect(() => {
    if (connectionStatus === 'CONNECTED') {
      onRefreshRooms();
    }
  }, [connectionStatus]);

  const handleJoinClick = (room: RoomListEntry) => {
    clearWsError();
    if (room.isLocked) {
      setSelectedRoom(room);
      setPassword('');
    } else {
      onJoinRoom(room.code, defaultPlayerName);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRoom && password) {
      onJoinRoom(selectedRoom.code, defaultPlayerName, password);
    }
  };

  const handleCancelPassword = () => {
    setSelectedRoom(null);
    setPassword('');
  };

  const commonButtonStyles: React.CSSProperties = {
    borderStyle: 'solid',
    borderWidth: '10px',
    borderImageSource: `url(${btnWood})`,
    borderImageSlice: '30 fill',
    textShadow: '1px 1px 2px black',
    fontFamily: 'Cinzel'
  };

  return (
    <div 
      className="w-full h-screen flex flex-col items-center justify-center animate-[fadeIn_0.5s_ease-out] bg-cover bg-center font-['Crimson_Text']"
      style={{ backgroundImage: `url(${tavernBg})` }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      <div className="relative z-10 flex flex-col items-center w-full max-w-2xl px-4">
        <h2 className="text-5xl font-['Cinzel'] font-black text-transparent bg-clip-text bg-gradient-to-b from-[#FFD700] via-[#FDB931] to-[#996515] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] mb-8">
          {t('multiplayer.joinRoom', language)}
        </h2>

        <div 
          className="flex flex-col gap-4 w-full p-8 rounded-xl shadow-2xl border-[6px] border-[#3E2723] outline outline-2 outline-[#DAA520]"
          style={{ backgroundImage: `url(${woodPanel})`, backgroundSize: 'cover' }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40 rounded-lg pointer-events-none"></div>

          {/* Room List Header */}
          <div className="relative z-10 flex items-center justify-between font-['Cinzel']">
            <span className="text-lg font-bold text-amber-200 uppercase tracking-wider">{t('multiplayer.availableRooms', language)}</span>
            <button
              type="button"
              onClick={onRefreshRooms}
              disabled={connectionStatus !== 'CONNECTED'}
              className="px-4 py-1 text-sm bg-[#5C4033] hover:bg-[#8B4513] text-amber-100 rounded border border-[#DAA520]/40 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              <span>↻</span> {t('multiplayer.refresh', language)}
            </button>
          </div>

          {/* Search Input */}
          <div className="relative z-10">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('multiplayer.searchPlaceholder', language)}
              className="w-full bg-black/60 border-2 border-[#8B4513] rounded-lg px-4 py-2 text-amber-100 focus:outline-none focus:border-[#DAA520] transition-colors placeholder-amber-900/50 text-lg shadow-inner"
            />
          </div>

          {/* Room List Container */}
          <div 
            className="relative z-10 max-h-[45vh] overflow-y-auto rounded-lg border-2 border-[#8B4513] p-1 shadow-inner bg-cover"
            style={{ backgroundImage: `url(${parchmentTexture})` }}
          >
            <div className="bg-white/10 backdrop-blur-[1px] min-h-full p-3">
              <RoomList
                rooms={filteredRooms}
                onJoin={handleJoinClick}
                language={language}
                isLoading={connectionStatus !== 'CONNECTED'}
                emptyMessage={searchQuery ? t('multiplayer.noRoomsMatch', language) : t('multiplayer.noRooms', language)}
              />
            </div>
          </div>

          {/* Password Modal Overlay */}
          {selectedRoom && (
            <div className="relative z-20 animate-[fadeIn_0.3s_ease-out] p-6 rounded-lg border-2 border-[#DAA520] bg-[#3E2723] shadow-2xl">
              <div className="text-amber-100 text-lg mb-4 font-['Cinzel'] font-bold">
                {t('multiplayer.enterPasswordFor', language)} <span className="text-cyan-400">{selectedRoom.roomName}</span>
              </div>
              <form onSubmit={handlePasswordSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('multiplayer.passwordPlaceholder', language)}
                  autoFocus
                  className="flex-1 bg-black/40 border-2 border-[#8B4513] rounded-lg px-4 py-2 text-amber-100 focus:outline-none focus:border-[#DAA520] transition-colors"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={!password}
                    className="px-6 py-2 bg-green-800 hover:bg-green-700 text-white font-bold rounded-lg border border-green-500/50 disabled:opacity-50"
                  >
                    {t('multiplayer.join', language)}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelPassword}
                    className="px-6 py-2 bg-[#5C4033] hover:bg-[#8B4513] text-amber-200 font-bold rounded-lg border border-[#DAA520]/20"
                  >
                    {t('multiplayer.cancel', language)}
                  </button>
                </div>
              </form>
            </div>
          )}

          {wsError && <p className="relative z-10 text-red-400 text-sm text-center mt-2 font-bold drop-shadow-md">{wsError}</p>}
          {connectionStatus !== 'CONNECTED' && (
            <p className="relative z-10 text-yellow-400 text-sm text-center mt-2 font-bold animate-pulse">
              {connectionStatus === 'CONNECTING' ? t('multiplayer.connecting', language) : t('multiplayer.disconnected', language)}
            </p>
          )}

          <button
            type="button"
            onClick={onBack}
            className="relative z-10 transition-all transform hover:scale-105 hover:brightness-110 text-amber-200/70 font-bold text-lg py-1 px-6 shadow-xl opacity-90"
            style={commonButtonStyles}
          >
            {t('multiplayer.back', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinRoomScreen;
